import React from 'react';
import { Loader2, ChevronDown } from 'lucide-react';

// --- Logo ---
export const WinBaghrafLogo: React.FC<{ className?: string, showText?: boolean }> = ({ className = "w-10 h-10", showText = true }) => (
  <div className="flex flex-col items-center">
    <svg viewBox="0 0 100 85" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Black Diamond - Positioned top center */}
      <path d="M50 0 L60 15 L50 30 L40 15 Z" fill="black" />
      
      {/* Red W Shape */}
      {/* Using a specific path to create the sharp W geometry */}
      <path 
        d="M10 25 L35 80 L50 45 L65 80 L90 25" 
        stroke="#C41E3A" 
        strokeWidth="10" 
        strokeLinecap="square" 
        strokeLinejoin="miter"
      />
      {/* Overlay to create the cut/interwoven effect if needed, but clean stroke usually works best for icons */}
    </svg>
  </div>
);

// --- Card ---
export const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 overflow-hidden ${className}`}>
    {children}
  </div>
);

// --- Mobile Card (optimized for touch) ---
export const MobileCard: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
  onClick?: () => void;
}> = ({ children, className = '', onClick }) => (
  <div 
    className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mobile-card ${onClick ? 'cursor-pointer active:bg-gray-50' : ''} ${className}`}
    onClick={onClick}
  >
    {children}
  </div>
);

// --- Button ---
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  fullWidthMobile?: boolean;
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  size = 'md',
  className = '', 
  isLoading,
  disabled,
  fullWidthMobile = false,
  ...props 
}) => {
  const baseStyles = "inline-flex items-center justify-center font-medium rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-target select-none-touch active:scale-[0.98]";
  
  const sizes = {
    sm: "px-3 py-2 text-xs min-h-[36px]",
    md: "px-4 py-2.5 text-sm min-h-[44px]",
    lg: "px-6 py-3.5 text-base min-h-[52px]"
  };

  const variants = {
    primary: "bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500 shadow-sm active:bg-indigo-800",
    secondary: "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 focus:ring-indigo-500 shadow-sm active:bg-gray-100",
    outline: "border border-gray-300 bg-transparent text-gray-700 hover:bg-gray-50 focus:ring-gray-500 active:bg-gray-100",
    ghost: "text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:ring-gray-500 active:bg-gray-200",
    danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 shadow-sm active:bg-red-800"
  };

  return (
    <button 
      className={`${baseStyles} ${sizes[size]} ${variants[variant]} ${fullWidthMobile ? 'w-full sm:w-auto' : ''} ${className}`} 
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
      {children}
    </button>
  );
};

// --- Professional Stacked Input ---
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  startIcon?: React.ElementType;
  startText?: string;
}

export const Input: React.FC<InputProps> = ({ label, error, startIcon: Icon, startText, className = '', ...props }) => {
  const hasStartContent = Icon || startText;
  return (
    <div className="w-full mb-4 sm:mb-5">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
      </label>
      <div className="relative">
        {hasStartContent && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            {Icon ? <Icon className="w-5 h-5" /> : <span className="text-sm font-medium">{startText}</span>}
          </div>
        )}
        <input
          className={`
            block w-full rounded-xl border bg-white text-gray-900 shadow-sm 
            focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 focus:outline-none
            placeholder:text-gray-400 h-12 sm:h-11 text-base sm:text-sm transition-colors duration-200
            ${hasStartContent ? 'pl-10 pr-3' : 'px-3'}
            ${error ? 'border-red-300 text-red-900 placeholder:text-red-300 focus:ring-red-200 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'}
            ${props.disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}
            ${className}
          `}
          {...props}
        />
        {error && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
             <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-xs text-red-600 flex items-center">
          {error}
        </p>
      )}
    </div>
  );
};

// --- Professional Stacked Select ---
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options?: { value: string; label: string }[];
  error?: string;
  startIcon?: React.ElementType;
  children?: React.ReactNode;
}

export const Select: React.FC<SelectProps> = ({ label, options, error, startIcon: Icon, children, className = '', ...props }) => {
  return (
    <div className="w-full mb-4 sm:mb-5">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
      </label>
      <div className="relative">
        {Icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <Icon className="w-5 h-5" />
          </div>
        )}
        <select
          className={`
            block w-full appearance-none rounded-xl border bg-white text-gray-900 shadow-sm 
            focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 focus:outline-none
            h-12 sm:h-11 text-base sm:text-sm transition-colors duration-200 cursor-pointer
            ${Icon ? 'pl-10 pr-10' : 'pl-3 pr-10'}
            ${error ? 'border-red-300 text-red-900 focus:ring-red-200 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'}
            ${className}
          `}
          {...props}
        >
          {children || options?.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        
        {/* Custom Chevron */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
          <ChevronDown className="w-5 h-5" />
        </div>
      </div>
      {error && (
        <p className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

// --- Badge ---
export const Badge: React.FC<{ children: React.ReactNode; color?: 'green' | 'blue' | 'indigo' | 'yellow' | 'gray' | 'red' | 'orange' | 'purple' }> = ({ children, color = 'gray' }) => {
  const colors = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200 ring-emerald-500/10',
    blue: 'bg-blue-50 text-blue-700 border-blue-200 ring-blue-500/10',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200 ring-indigo-500/10',
    yellow: 'bg-amber-50 text-amber-700 border-amber-200 ring-amber-500/10',
    gray: 'bg-gray-50 text-gray-600 border-gray-200 ring-gray-500/10',
    red: 'bg-rose-50 text-rose-700 border-rose-200 ring-rose-500/10',
    orange: 'bg-orange-50 text-orange-700 border-orange-200 ring-orange-500/10',
    purple: 'bg-purple-50 text-purple-700 border-purple-200 ring-purple-500/10'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ring-1 ring-inset ${colors[color]}`}>
      {children}
    </span>
  );
};

// --- Mobile List Item ---
interface MobileListItemProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  showChevron?: boolean;
}

export const MobileListItem: React.FC<MobileListItemProps> = ({ 
  children, 
  onClick, 
  className = '',
  showChevron = true 
}) => (
  <div 
    className={`flex items-center justify-between px-4 py-3.5 list-item-native ${onClick ? 'cursor-pointer' : ''} ${className}`}
    onClick={onClick}
  >
    <div className="flex-1 min-w-0">{children}</div>
    {showChevron && onClick && (
      <ChevronDown className="w-5 h-5 text-gray-300 -rotate-90 shrink-0 ml-2" />
    )}
  </div>
);

// --- Empty State ---
interface EmptyStateProps {
  icon?: React.ElementType;
  title: string;
  description?: string;
  action?: React.ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ 
  icon: Icon, 
  title, 
  description, 
  action 
}) => (
  <div className="empty-state">
    {Icon && (
      <div className="empty-state-icon">
        <Icon className="w-full h-full text-gray-300" />
      </div>
    )}
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    {description && (
      <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">{description}</p>
    )}
    {action}
  </div>
);

// --- Skeleton Loader ---
export const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`skeleton ${className}`} />
);

// --- Floating Action Button ---
interface FABProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ElementType;
  label?: string;
}

export const FloatingActionButton: React.FC<FABProps> = ({ 
  icon: Icon, 
  label,
  className = '',
  ...props 
}) => (
  <button
    className={`fixed bottom-20 right-4 lg:hidden fab bg-indigo-600 text-white flex items-center justify-center z-30 ${className}`}
    {...props}
  >
    <Icon className="w-6 h-6" />
    {label && <span className="sr-only">{label}</span>}
  </button>
);